import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAccounts, accountTypeKey } from "@/lib/AccountContext";
import { fmtMoney } from "@/lib/format";
import { ArrowRight, ArrowLeftRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function InternalTransfer() {
  const { t } = useTranslation();
  const { accounts, selectedAccount, refreshAccounts } = useAccounts();
  const [fromId, setFromId] = useState(selectedAccount?.id || accounts[0]?.id || "");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [concept, setConcept] = useState("");
  const [fx, setFx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const label = (a) => a.label || t(accountTypeKey(a.account_type));
  const from = accounts.find((a) => a.id === fromId) || null;
  const to = accounts.find((a) => a.id === toId) || null;

  // Pick a sensible default destination (first account that isn't the source).
  useEffect(() => {
    if (!toId || toId === fromId) {
      const other = accounts.find((a) => a.id !== fromId);
      if (other) setToId(other.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromId, accounts]);

  // FX preview when source & destination currencies differ.
  useEffect(() => {
    const amt = parseFloat(amount) || 0;
    if (!amt || !from || !to || from.currency === to.currency) { setFx(null); return; }
    let active = true;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/fx/convert", { amount: amt, from_currency: from.currency, to_currency: to.currency });
        if (active) setFx(data);
      } catch { if (active) setFx(null); }
    }, 350);
    return () => { active = false; clearTimeout(id); };
  }, [amount, from, to]);

  const insufficient = from && parseFloat(amount) > from.balance;

  const submit = async (e) => {
    e.preventDefault();
    if (!from || !to) { toast.error(t("transfers.internal_pick")); return; }
    if (from.id === to.id) { toast.error(t("transfers.internal_same_account")); return; }
    if (!(parseFloat(amount) > 0)) { toast.error(t("transfers.internal_amount_invalid")); return; }
    setSubmitting(true); setResult(null);
    try {
      const { data } = await api.post("/transfers/internal", {
        from_account_id: from.id, to_account_id: to.id,
        amount: parseFloat(amount), concept: concept.trim(),
      });
      setResult(data);
      toast.success(`${t("transfers.internal_success")} · ${fmtMoney(data.credited, data.credited_currency)}`);
      setAmount(""); setConcept("");
      await refreshAccounts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const selectCls = "w-full px-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none";

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-7 space-y-5 max-w-2xl" data-testid="internal-transfer-form">
      <div className="flex items-center gap-3 text-slate-900">
        <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><ArrowLeftRight size={18} /></span>
        <div>
          <div className="font-display font-bold text-lg">{t("transfers.tab_internal")}</div>
          <div className="text-xs text-slate-500">{t("transfers.tab_internal_desc")}</div>
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.internal_from")}</span>
        <select value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="it-from" className={`mt-1.5 ${selectCls}`}>
          {accounts.map((a) => <option key={a.id} value={a.id}>{label(a)} · {fmtMoney(a.balance, a.currency)}</option>)}
        </select>
      </label>

      <div className="flex justify-center"><ArrowRight size={18} className="text-slate-400 rotate-90" /></div>

      <label className="block">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.internal_to")}</span>
        <select value={toId} onChange={(e) => setToId(e.target.value)} data-testid="it-to" className={`mt-1.5 ${selectCls}`}>
          {accounts.filter((a) => a.id !== fromId).map((a) => <option key={a.id} value={a.id}>{label(a)} · {fmtMoney(a.balance, a.currency)}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.internal_amount")} {from ? `(${from.currency})` : ""}</span>
        <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="it-amount"
          className={`mt-1.5 w-full px-4 py-3 rounded-xl border font-mono-fin focus:ring-4 outline-none ${insufficient ? "border-red-300 focus:ring-red-100" : "border-slate-200 focus:border-blue-600 focus:ring-blue-100"}`} />
        {insufficient && <span className="text-[11px] text-red-600 mt-1 block">{t("transfers.internal_insufficient")}</span>}
      </label>

      {fx && (
        <div className="text-sm rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900 flex items-center gap-2" data-testid="it-fx">
          <ArrowRight size={15} className="text-blue-600 shrink-0" />
          <span>{t("transfers.internal_will_credit")}: <b>{fmtMoney(fx.converted_amount, to?.currency)}</b> · 1 {from?.currency} = {fx.rate} {to?.currency}</span>
        </div>
      )}

      <label className="block">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.concept")}</span>
        <input value={concept} onChange={(e) => setConcept(e.target.value)} data-testid="it-concept"
          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
      </label>

      <button type="submit" disabled={submitting || insufficient} data-testid="it-submit"
        className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold disabled:opacity-60 shadow-[0_10px_25px_-8px_rgba(37,99,235,0.55)] transition-all"
        style={{ background: "linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)" }}>
        {submitting ? t("transfers.internal_processing") : <>{t("transfers.internal_submit")} <ArrowRight size={18} /></>}
      </button>

      {result && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2" data-testid="it-result">
          <CheckCircle2 size={16} /> {result.reference} · {fmtMoney(result.debited, result.debited_currency)} → {fmtMoney(result.credited, result.credited_currency)}
        </div>
      )}
    </form>
  );
}
